import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "../customAxios.js";
import * as XLSX from "xlsx";
import {
  Autocomplete,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  Paper,
  Chip,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import InputMask from "react-input-mask";
import { getImagesUrls } from "../utils/s3Utils.js";
import * as EmailValidator from "email-validator";
import { regionsAndCommunes } from "../helpers/ChileanAddressesData";
import ProductList from "./ProductList";
import {
  orderTypeEnum,
  shippingMethodEnum,
  pickUpMethodEnum,
} from "../helpers/enums";
import LoadingComponent from "./LoadingComponent";
import AlertBox from "./AlertBox";
import CustomDropzone from "./CustomDropzone";
import { useImagePreloader } from "../hooks/useImagePreLoader.jsx";
import { useAuth } from "../hooks/useAuth.jsx";

//GET URL PRESIGNED OF IMAGES
//dict with images key routes of the page
const imageskeyRoutes = {
  s3massiveUploadIcon: "assets/icons/massive-upload.png",
  s3manualUploadIcon: "assets/icons/manual-upload.png",
};

const imageUrls = await getImagesUrls(imageskeyRoutes, "uploadRequests");

// Add this constant near the top with other constants
const WAREHOUSE_OPTIONS = ["WH1", "WH2", "WH3"];

function UploadRequests(props) {
  const { user } = useAuth();
  const { allImagesLoaded, timeoutReached } = useImagePreloader(
    imageUrls,
    5000
  );
  const [selectedFileType, setSelectedFileType] = useState("");
  const [selectedShippingMethod, setSelectedShippingMethod] = useState(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState("");
  const [files, setFiles] = useState([]);
  const [openFileErrorAlert, setOpenFileErrorAlert] = useState(false);
  const [validFile, setValidFile] = useState(false);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);

  // Manual form
  const [referenceId, setReferenceId] = useState("");
  const [recipient, setRecipient] = useState("");
  const [street, setStreet] = useState("");
  const [streetNumber, setStreetNumber] = useState("");
  const [selectedCommune, setSelectedCommune] = useState(null);
  const [communesOptions, setCommunesOptions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedOrderType, setSelectedOrderType] = useState(null);
  const [notes, setNotes] = useState("");
  const [contactRut, setContactRut] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [packages, setPackages] = useState("");
  const [weight, setWeight] = useState("");
  const [measures, setMeasures] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [sellerOptions, setSellerOptions] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [selectedFromWarehouse, setselectedFromWarehouse] = useState(null);
  const [selectedToWarehouse, setselectedToWarehouse] = useState(null);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [sellerWarehouseOptions, setSellerWarehouseOptions] = useState([]);
  const [communeWarehouses, setCommuneWarehouses] = useState([]);
  const [submitPressed, setSubmitPressed] = useState(false);
  const [openSuccessAlert, setOpenSuccessAlert] = useState(false);
  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [messageAlert, setMessageAlert] = useState("");

  // Products
  const [productOptions, setProductOptions] = useState([]);
  const [selectedProductList, setSelectedProductList] = useState([]);
  const [productSaved, setProductSaved] = useState(false);

  //get user type
  const isOperator = user.user_type.includes("operator");
  const isSeller = user.user_type.includes("seller");
  // next admin section steps:
  const isAdmin = user.user_type.includes("admin");

  const urlSeller =
    "https://go-ex-v2-public.s3.amazonaws.com/templates/miplanillaseller.xlsx";
  const urlOperator =
    "https://go-ex-v2-public.s3.amazonaws.com/templates/miplanillaoperator.xlsx";

  const urlExcel = isAdmin || isSeller ? urlSeller : urlOperator;

  //useSate to show that there are no warehouses with availability
  const [warehouseAvailability, setWarehouseAvailability] = useState(false);

  //useState to catch when warehouse loaded from backend
  const [warehouseLoaded, setWarehouseLoaded] = useState(false);

  let navigate = useNavigate();

  const regionsOptions = regionsAndCommunes.map((region) => ({
    value: region.value,
    label: region.label,
  }));

  const orderTypeOptions = orderTypeEnum.map((orderType) => ({
    value: orderType.value,
    label: orderType.label,
  }));

  //FILE VALIDATION FUNCTIONS

  //this consider the first file on the files list, for now we are only allowing one file to be uploaded
  useEffect(() => {
    setFile(files[0]);
  }, [files]);

  async function uploadOrdersExcel() {
    const selectedFile = file;
    var formData = new FormData();
    formData.append("excel_file", selectedFile);
    if (isSeller && selectedWarehouse) {
      formData.append("warehouse", selectedWarehouse);
    }

    try {
      await axios.post("upload_excel_orders/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMessageAlert("Archivo cargado con éxito");
      setOpenSuccessAlert(true);
    } catch (error) {
      console.error(`Error al cargar el archivo: ${error}`);
      let errorMessage = "Error al cargar el archivo";
      if (
        error?.response &&
        error?.response?.data &&
        error?.response?.data?.error
      ) {
        errorMessage += ": " + error?.response?.data?.error;
      }
      setMessageAlert(errorMessage);
      setOpenErrorAlert(true);
    } finally {
      setFiles([]);
      setFile(null);
      setSelectedWarehouse("");
    }
  }

  useEffect(() => {
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        //get the two return values of function validateExcel
        const [isValid, message] = validateExcel(json);
        setMessageAlert(message);

        if (isValid) {
          console.log("File is valid");
          setValidFile(true);
        } else {
          console.error("File is invalid");
          setOpenFileErrorAlert(true);
          setValidFile(false);
          setFiles([]);
        }
      };
      reader.readAsBinaryString(file);
    }
  }, [file]); // Dependency array to re-run the effect when `file` changes

  const fetchSellers = async () => {
    try {
      const { data } = await axios.get("admins/sellers/");
      setSellerOptions(data);
    } catch (error) {
      console.error(`Error al cargar sellers: ${error}`);
    }
  };

  const fetchSellerWarehouses = async (seller_id) => {
    try {
      const { data } = await axios.get(`sellers/warehouses/${seller_id}/`);
      setSellerWarehouseOptions(data);
    } catch (error) {
      console.error(`Error al cargar la bodegas asociadas al seller: ${error}`);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const { data } = await axios.get("sellers/warehouses/");
      setWarehouseOptions(data);
    } catch (error) {
      console.error(`Error al cargar las bodegas asociadas: ${error}`);
    }
  };

  //functions to validate excel file to load massive orders
  const validateExcel = (data) => {
    const requiredHeaders = isSeller
      ? [
          "Id_referencia",
          "Destinatario",
          "Direccion",
          "Comuna",
          "Region",
          "Notas",
          "Rut",
          "Telefono",
          "Correo",
          "Bultos",
          "Tags",
        ]
      : [
          "Id_referencia",
          "Destinatario",
          "Direccion",
          "Comuna",
          "Region",
          "Notas",
          "Rut",
          "Telefono",
          "Correo",
          "Bultos",
          "Tags",
          "Seller_id",
        ];

    if (data.length === 0 || !data[0]) {
      return [false, "Archivo vacío, no se entregaron ordenes a cargar."];
    }

    if (isSeller && !selectedWarehouse) {
      return [false, "Debe seleccionar una bodega para cargar las órdenes"];
    }

    const headers = Object.keys(data[0]);
    const hasRequiredHeaders = requiredHeaders.every((header) =>
      headers.includes(header)
    );
    if (!hasRequiredHeaders) {
      console.log("Missing at least one required header");
      return [false, "Missing at least one required header"];
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const rutRegex = /^\d{1,3}(?:\.\d{1,3}){2}-[\dkK]$/; // Basic RUT validation pattern

    for (const row of data) {
      // Validate Rut if it exists
      if (row["Rut"] && !rutRegex.test(row["Rut"])) {
        console.log("formato de rut inválido");

        return [
          false,
          `formato de rut inválido en fila ${data.indexOf(row) + 2}`,
        ];
      }
      // Vaalidate if Id_referencia exists
      if (!row["Id_referencia"]) {
        console.log("Id_referencia inválido");
        return [
          false,
          `Id_referencia inválido en fila ${data.indexOf(row) + 2}`,
        ];
      }

      // Validate Destinatario exists
      if (!row["Destinatario"].trim()) {
        console.log("Attributo Destinatario inválido");
        return [
          false,
          `Attributo Destinatario inválido en fila ${data.indexOf(row) + 2}`,
        ];
      }

      // Validate Email if it exists
      if (row["Correo"] && !emailRegex.test(row["Correo"])) {
        console.log("Formato de correo inválido");
        return [
          false,
          `Formato de correo inválido en fila ${data.indexOf(row) + 2}`,
        ];
      }

      // Validate Direction exists
      if (!row["Direccion"].trim()) {
        console.log("Attributo Direccion inválido");
        return [
          false,
          `Attributo Direccion inválido en fila ${data.indexOf(row) + 2}`,
        ];
      }

      //validate existance of comuna
      if (!row["Comuna"].trim()) {
        console.log("Attributo Comuna inválido");
        return [
          false,
          `Attributo Comuna inválido en fila ${data.indexOf(row) + 2}`,
        ];
      }

      //validate existance of region
      if (!row["Region"].trim()) {
        console.log("Attributo Region inválido");
        return [
          false,
          `Attributo Region inválido en fila ${data.indexOf(row) + 2}`,
        ];
      }

      // Validate Bultos as a positive integer
      if (!(Number.isInteger(row["Bultos"]) && row["Bultos"] > 0)) {
        console.log("Attributo Bultos inválido");
        return [
          false,
          `Attributo Bultos inválido en fila ${data.indexOf(row) + 2}`,
        ];
      }

      //Validate that, if user is operator, seller id exists and it is a number
      if (isOperator) {
        if (!Number.isInteger(row["Seller_id"])) {
          console.log("Attributo Seller_id inválido");
          return [
            false,
            `Attributo Seller_id inválido en fila ${data.indexOf(row) + 2}`,
          ];
        }
      }

      //Validate that, if user is seller, warehouse id exists and it is a number
      if (isSeller) {
        if (!Number.isInteger(row["Bodega_id"])) {
          console.log("Attributo Seller_id inválido");
          return [
            false,
            `Attributo Bodega_id inválido en fila ${data.indexOf(row) + 2}`,
          ];
        }
      }
    }
    return [true, "File is valid"];
  };

  const fetchCompanies = async () => {
    if (isSeller) {
      try {
        const { data } = await axios.get("companies/seller_company/");
        setCompanyOptions([data]);
      } catch (error) {
        console.error(`Error al cargar los sellers de la compañia: ${error}`);
      }
    } else if (isAdmin) {
      try {
        const { data } = await axios.get("companies/admin_companies/");
        setCompanyOptions(data);
      } catch (error) {
        console.error(`Error al cargar los admin de la compañia: ${error}`);
      }
    }
  };

  const fetchProductsOptions = async (company_id, order_type) => {
    try {
      const { data } = await axios.get(
        `products/company_products/${company_id}/${order_type}/`
      );
      if (
        order_type === "delivery_fulfillment" ||
        order_type === "pick_up_fulfillment"
      ) {
        const filteredProducts = data.filter(
          (product) => product.ideal_stock > 0
        );
        setProductOptions(filteredProducts);
      } else {
        setProductOptions(data);
      }
    } catch (error) {
      console.error(`Error al cargar los productos de la compañia: ${error}`);
    }
  };

  const handleSelectCompany = (event, newValue) => {
    event.preventDefault();
    setSelectedCompany(newValue);
    setSelectedProductList([]);
    setProductOptions([]);
    if (newValue !== null && selectedOrderType && selectedOrderType.value) {
      fetchProductsOptions(newValue.id, selectedOrderType.value);
    }
  };

  const handleSelectFromWarehouse = (event, newValue) => {
    event.preventDefault();
    setselectedFromWarehouse(newValue);
  };

  const handleSelectToWarehouse = (event, newValue) => {
    event.preventDefault();
    setselectedToWarehouse(newValue);
  };

  const handleSelectRegion = (event, newValue) => {
    event.preventDefault();
    setSelectedRegion(newValue);
    setSelectedCommune(null);
    const selectedRegionData = regionsAndCommunes.find(
      (region) => region.value === (newValue ? newValue.value : "")
    );
    const newCommunesOptions = selectedRegionData
      ? selectedRegionData.communes.map((commune) => ({
          value: commune.value,
          label: commune.label,
        }))
      : [];
    setCommunesOptions(newCommunesOptions);
  };

  const handleSelectOrderType = (event, newValue) => {
    event.preventDefault();
    setSelectedOrderType(newValue);
    setSelectedShippingMethod(null);
    setselectedFromWarehouse(null);
    setselectedToWarehouse(null);
    setCommuneWarehouses([]);
    setSelectedCommune(null);
    setSelectedRegion(null);
    setProductSaved(false);
    setWarehouseLoaded(false);
    setSelectedProductList([]);
    if (newValue !== null && selectedCompany !== null) {
      fetchProductsOptions(selectedCompany.id, newValue.value);
    }

    // const selectedOrderTypeData = orderTypeEnum.find(orderType => orderType.value === ( newValue ? newValue.value: ""));
  };

  const handleSelectCommune = async (event, newValue) => {
    event.preventDefault();
    setCommuneWarehouses([]);
    setselectedToWarehouse(null);
    setSelectedCommune(newValue);
    if (
      selectedOrderType.value === "pick_up_fulfillment" ||
      selectedOrderType.value === "pick_up" ||
      selectedOrderType.value === "delivery_return"
    ) {
      if (
        selectedShippingMethod?.value === "pick_up_bluex" ||
        selectedOrderType.value === "delivery_return"
      ) {
        try {
          const { data } = await axios.get(
            `warehouses/bluex_agencies/${newValue.value}/`
          );
          setCommuneWarehouses(data);
        } catch (error) {
          console.error(`Error al cargar agencias de bluexpress: ${error}`);
        }
      } else if (selectedShippingMethod?.value === "pick_up_goex") {
        try {
          const { data } = await axios.get(
            `warehouses/commune_warehouses/${newValue.value}/`
          );
          setCommuneWarehouses(data);
        } catch (error) {
          console.error(
            `Error al cargar agencias de comunales de bluexpress: ${error}`
          );
        }
      }
    }
  };

  const serializeProducts = (productsData) => {
    const serializedProducts = productsData.map((product) => ({
      sku: product.sku,
      quantity: product.quantity,
    }));
    return serializedProducts;
  };

  async function handleSubmit() {
    setSubmitPressed(true);
    if (
      selectedFileType === "Carga manual" &&
      referenceId !== "" &&
      recipient !== "" &&
      (selectedOrderType.value === "pick_up" ||
        selectedOrderType.value === "pick_up_fulfillment" ||
        selectedOrderType.value === "delivery_return" ||
        street !== "") &&
      (selectedOrderType.value === "pick_up" ||
        selectedOrderType.value === "pick_up_fulfillment" ||
        selectedOrderType.value === "delivery_return" ||
        streetNumber !== "") &&
      (selectedOrderType.value === "click_collect" ||
        selectedCommune !== null) &&
      (selectedOrderType.value === "click_collect" ||
        selectedRegion !== null) &&
      contactRut !== "" &&
      contactPhone !== "" &&
      contactEmail !== "" &&
      EmailValidator.validate(contactEmail) &&
      // (selectedOrderType.value !== "delivery" || packages !== "" ) &&
      ((selectedOrderType.value !== "delivery" &&
        selectedOrderType.value !== "delivery_fulfillment" &&
        selectedOrderType.value !== "pick_up" &&
        selectedOrderType.value !== "pick_up_fulfillment") ||
        packages !== "") &&
      // (selectedOrderType.value !== "delivery" || measures !== "" ) &&
      (selectedCompany !== null || !isOperator) &&
      ((selectedOrderType.value !== "delivery" &&
        selectedOrderType.value !== "delivery_fulfillment" &&
        selectedOrderType.value !== "pick_up" &&
        selectedOrderType.value !== "pick_up_fulfillment") ||
        selectedShippingMethod !== null) &&
      (selectedOrderType.value === "delivery" ||
        selectedOrderType.value === "pick_up" ||
        selectedOrderType.value === "delivery_return" ||
        selectedProductList.length > 0) &&
      ((selectedOrderType.value !== "pick_up" &&
        selectedOrderType.value !== "pick_up_fulfillment") ||
        selectedToWarehouse !== null) &&
      ((selectedOrderType.value !== "click_collect" &&
        selectedOrderType.value !== "pay_collect" &&
        selectedOrderType.value !== "delivery_return") ||
        selectedFromWarehouse !== null)
    ) {
      setLoading(true);
      try {
        const { data } = await axios.post("create_orders/", {
          data: [
            {
              service: "sale",
              type: selectedOrderType.value,
              reference_id: referenceId,
              recipient: recipient,
              street: street,
              street_number: streetNumber,
              commune: selectedCommune?.value,
              region: selectedRegion?.value,
              notes: notes,
              contact_rut: contactRut,
              contact_phone: contactPhone,
              contact_email: contactEmail,
              packages: packages,
              weight: weight,
              measures: measures,
              products: serializeProducts(selectedProductList),
              company_id: selectedCompany ? selectedCompany.id : null,
              shipping_method: selectedShippingMethod
                ? selectedShippingMethod.value
                : "not_selected",
              from_warehouse_id: selectedFromWarehouse
                ? selectedFromWarehouse.id
                : null,
              to_warehouse_id: selectedToWarehouse
                ? selectedToWarehouse.id
                : null,
            },
          ],
        });
        console.log(data);
        setLoading(false);
        setMessageAlert("Orden creada con éxito");
        setOpenSuccessAlert(true);
        clearData();
        navigate("/home/upload-success-order");
      } catch (error) {
        console.error(`Error al crear la orden: ${error}`);
        setLoading(false);
        setMessageAlert("Error al crear la orden");
        setOpenErrorAlert(true);
      }
    } else {
      setMessageAlert("Faltan campos obligatorios");
      setOpenErrorAlert(true);
    }
  }

  async function handleFileSubmit() {
    uploadOrdersExcel();
  }

  const handleDelete = (path) => {
    setFiles(files.filter((file) => file.path !== path));
  };

  const clearData = () => {
    setFiles([]);
    setReferenceId("");
    setRecipient("");
    setStreet("");
    setStreetNumber("");
    setSelectedCommune(null);
    setSelectedRegion(null);
    setNotes("");
    setContactRut("");
    setContactPhone("");
    setContactEmail("");
    setPackages("");
    setWeight("");
    setMeasures("");
    setSelectedCompany(null);
    setSelectedShippingMethod(null);
    setselectedFromWarehouse(null);
    setSubmitPressed(false);
    setSelectedProductList([]);
    setProductOptions([]);
    setProductSaved(false);
    setWarehouseLoaded(false);
    setCommuneWarehouses([]);
    setselectedToWarehouse(null);
  };

  const handleFileDrop = (acceptedFiles) => {
    // Manejar los archivos aquí
    console.log("Archivos subidos:", acceptedFiles);
    acceptedFiles.forEach((file) => {
      setFiles((prevFiles) => {
        return [...prevFiles, file];
      });
    });
  };

  useEffect(() => {
    if (isAdmin) {
      fetchSellers();
    }
    if (isSeller) {
      fetchWarehouses();
    }
  }, [isAdmin, isSeller]);

  return (
    <>
      {!allImagesLoaded && !timeoutReached ? (
        <LoadingComponent />
      ) : (
        <>
          <Grid item xs={12}>
            <h1>Carga de Ordenes</h1>
          </Grid>
          <Grid item xs={12}>
            <FormControl>
              <Link to="/home" className="back-login-btn">
                <i className="material-icons-outlined forgotten-back-arrow">
                  arrow_back
                </i>
                <p>Volver atrás</p>
              </Link>
            </FormControl>
          </Grid>
          {/* following is long */}
          <Grid
            container
            className="select-ecommerce-container"
            style={{ marginBottom: "15px" }}
          >
            <Grid item xs={12} sm={12} className="flex-center">
              <h5>Tipo de carga</h5>
            </Grid>
            <Grid sx={{ flexGrow: 1 }} container>
              <Grid item xs={12}>
                <Grid container justifyContent="center">
                  <Grid item className="option first-row">
                    <Paper
                      elevation={0}
                      className={`select-option ${
                        selectedFileType === "Carga masiva" ? "selected" : ""
                      }`}
                      onClick={() => {
                        clearData();
                        setSelectedFileType("Carga masiva");
                      }}
                    >
                      <img
                        src={imageUrls.s3massiveUploadIcon}
                        alt="carga masiva"
                      />
                      <p>Carga masiva</p>
                    </Paper>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            {selectedFileType === "Carga masiva" && (
              <Grid container className="container-upload-files">
                <Grid item xs={12} className="flex-center">
                  <h5>Carga tu orden</h5>
                </Grid>
                <Grid item xs={12} className="flex-center">
                  <p className="mg-0">
                    {"Seleccionaste hacer una carga "}
                    <span className="fw-medium">{selectedFileType}</span>
                  </p>
                </Grid>
                {selectedFileType === "Carga masiva" && (
                  <Grid item xs={12} className="flex-center download-container">
                    <p>{"Si no tienes a mano el archivo, puedes "}</p>
                    <a
                      href={urlExcel}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                    >
                      <p>{" descargar la plantilla"}</p>
                    </a>
                  </Grid>
                )}
                {isAdmin && (
                  <Grid item xs={12} className="flex-center">
                    <Autocomplete
                      fullWidth
                      id="seller-select"
                      options={sellerOptions}
                      getOptionLabel={(seller) =>
                        seller ? seller.company.social_reason : ""
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Seller"
                          helperText={
                            submitPressed &&
                            selectedSeller === null &&
                            "Campo obligatorio"
                          }
                          error={submitPressed && selectedSeller === null}
                        />
                      )}
                      value={selectedSeller}
                      onChange={(event, newValue) =>
                        setSelectedSeller(event, newValue)
                      }
                    />
                  </Grid>
                )}

                {isSeller && (
                  <Grid item xs={12} className="flex-center">
                    <Autocomplete
                      fullWidth
                      id="warehouse-select"
                      options={warehouseOptions}
                      getOptionLabel={(warehouse) => warehouse || ""}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Warehouse"
                          helperText={
                            submitPressed &&
                            selectedWarehouse === "" &&
                            "Campo obligatorio"
                          }
                          error={submitPressed && selectedWarehouse === ""}
                        />
                      )}
                      value={selectedWarehouse}
                      onChange={(event, newValue) =>
                        setSelectedWarehouse(newValue)
                      }
                    />
                  </Grid>
                )}
              </Grid>
            )}
            {selectedFileType === "Carga manual" && (
              <>
                <Grid container style={{ marginTop: "20px" }}>
                  <Grid item xs={12} className="flex-center">
                    <Grid container className="manual-upload-container">
                      <Grid item xs={12} sm={6} className="pd-r-10 pd-l-10">
                        <Autocomplete
                          fullWidth
                          id="company-select"
                          options={companyOptions}
                          getOptionLabel={(company) =>
                            company ? company.social_reason : ""
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Seller"
                              helperText={
                                submitPressed &&
                                selectedCompany === null &&
                                "Campo obligatorio"
                              }
                              error={submitPressed && selectedCompany === null}
                              disabled={productSaved}
                            />
                          )}
                          value={selectedCompany}
                          onChange={(event, newValue) =>
                            handleSelectCompany(event, newValue)
                          }
                          disabled={productSaved}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} className="pd-r-10 pd-l-10">
                        <Autocomplete
                          fullWidth
                          id="order-type-select"
                          options={orderTypeOptions}
                          getOptionLabel={(orderType) =>
                            orderType ? orderType.label : ""
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Tipo de orden"
                              helperText={
                                submitPressed &&
                                selectedOrderType === null &&
                                "Campo obligatorio"
                              }
                              error={
                                submitPressed && selectedOrderType === null
                              }
                              disabled={productSaved}
                            />
                          )}
                          value={selectedOrderType}
                          onChange={(event, newValue) =>
                            handleSelectOrderType(event, newValue)
                          }
                          disabled={productSaved}
                        />
                      </Grid>
                      {selectedOrderType &&
                        selectedOrderType.value !== "delivery" &&
                        selectedOrderType.value !== "pick_up" &&
                        selectedOrderType.value !== "delivery_return" && (
                          <Grid item xs={12} sm={12}>
                            <ProductList
                              setSelectedProductList={setSelectedProductList}
                              selectedProductList={selectedProductList}
                              productOptions={productOptions}
                              productSaved={productSaved}
                              orderType={selectedOrderType}
                            />
                          </Grid>
                        )}
                    </Grid>
                  </Grid>
                </Grid>
              </>
            )}

            {selectedOrderType &&
              (productSaved ||
                selectedOrderType.value === "delivery" ||
                selectedOrderType.value === "pick_up" ||
                selectedOrderType.value === "delivery_return") &&
              selectedFileType === "Carga manual" &&
              ((selectedOrderType.value !== "click_collect" &&
                selectedOrderType.value !== "pay_collect") ||
                warehouseAvailability) && (
                //adjust itam at center
                <>
                  <Grid container>
                    <Grid item xs={12} className="flex-center">
                      <Grid container className="manual-upload-container">
                        {selectedOrderType.value !== "click_collect" &&
                          selectedOrderType.value !== "pay_collect" && (
                            <>
                              {selectedOrderType.value !==
                                "delivery_return" && (
                                <Grid
                                  item
                                  xs={12}
                                  sm={6}
                                  className="pd-r-10 pd-l-10"
                                >
                                  <Autocomplete
                                    fullWidth
                                    id="shipping-select"
                                    options={
                                      selectedOrderType.value === "pick_up" ||
                                      selectedOrderType.value ===
                                        "pick_up_fulfillment"
                                        ? pickUpMethodEnum
                                        : shippingMethodEnum
                                    }
                                    getOptionLabel={(shippingMethod) =>
                                      shippingMethod ? shippingMethod.label : ""
                                    }
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label="Tipo de Envío"
                                        helperText={
                                          submitPressed &&
                                          selectedShippingMethod == null &&
                                          "Campo obligatorio"
                                        }
                                        error={
                                          submitPressed &&
                                          selectedShippingMethod == null
                                        }
                                      />
                                    )}
                                    value={selectedShippingMethod}
                                    onChange={(event, newValue) => {
                                      setSelectedCommune(null);
                                      setSelectedRegion(null);
                                      if (selectedToWarehouse) {
                                        setselectedToWarehouse(null);
                                      }
                                      setSelectedShippingMethod(newValue);
                                    }}
                                  />
                                </Grid>
                              )}

                              <Grid
                                item
                                xs={12}
                                sm={6}
                                className="pd-r-10 pd-l-10"
                              >
                                <Autocomplete
                                  fullWidth
                                  id="region-select"
                                  options={regionsOptions}
                                  getOptionLabel={(region) =>
                                    region ? region.label : ""
                                  }
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label={
                                        selectedOrderType.value === "pick_up" ||
                                        selectedOrderType.value ===
                                          "pick_up_fulfillment"
                                          ? "Región punto Pick Up"
                                          : "Región"
                                      }
                                      helperText={
                                        submitPressed &&
                                        selectedRegion === null &&
                                        "Campo obligatorio"
                                      }
                                      error={
                                        submitPressed && selectedRegion === null
                                      }
                                    />
                                  )}
                                  value={selectedRegion}
                                  onChange={(event, newValue) =>
                                    handleSelectRegion(event, newValue)
                                  }
                                />
                              </Grid>
                              <Grid
                                item
                                xs={12}
                                sm={6}
                                className="pd-r-10 pd-l-10"
                              >
                                <Autocomplete
                                  fullWidth
                                  id="communes-select"
                                  options={communesOptions}
                                  getOptionLabel={(commune) =>
                                    commune ? commune.label : ""
                                  }
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label={
                                        selectedOrderType.value === "pick_up" ||
                                        selectedOrderType.value ===
                                          "pick_up_fulfillment"
                                          ? "Comuna punto Pick Up"
                                          : "Comuna"
                                      }
                                      helperText={
                                        submitPressed &&
                                        selectedCommune === null &&
                                        "Campo obligatorio"
                                      }
                                      error={
                                        submitPressed &&
                                        selectedCommune === null
                                      }
                                    />
                                  )}
                                  value={selectedCommune}
                                  onChange={(event, newValue) =>
                                    handleSelectCommune(event, newValue)
                                  }
                                />
                              </Grid>
                            </>
                          )}
                        {(selectedOrderType.value === "pick_up" ||
                          selectedOrderType.value ===
                            "pick_up_fulfillment") && (
                          <Grid item xs={12} sm={6} className="pd-r-10 pd-l-10">
                            <Tooltip
                              title={
                                communeWarehouses.length > 0
                                  ? ""
                                  : "Debe seleccionar comuna con puntos de retiro disponibles"
                              }
                              disableHoverListener={
                                communeWarehouses.length > 0
                              }
                            >
                              <Autocomplete
                                fullWidth
                                id="to-warehouse-select"
                                options={communeWarehouses}
                                getOptionLabel={(warehouse) =>
                                  warehouse
                                    ? `${warehouse.name}, ${warehouse.address}`
                                    : ""
                                }
                                disabled={communeWarehouses.length === 0}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Bodega de destino Pick Up"
                                    helperText={
                                      submitPressed &&
                                      selectedToWarehouse === null &&
                                      "Campo obligatorio"
                                    }
                                    error={
                                      submitPressed &&
                                      selectedToWarehouse === null
                                    }
                                  />
                                )}
                                value={selectedToWarehouse}
                                onChange={(event, newValue) =>
                                  handleSelectToWarehouse(event, newValue)
                                }
                              />
                            </Tooltip>
                          </Grid>
                        )}
                        {selectedOrderType.value === "delivery_return" && (
                          <Grid item xs={12} sm={6} className="pd-r-10 pd-l-10">
                            <Tooltip
                              title={
                                communeWarehouses.length > 0
                                  ? ""
                                  : "Debe seleccionar comuna con puntos de retiro disponibles"
                              }
                              disableHoverListener={
                                communeWarehouses.length > 0
                              }
                            >
                              <Autocomplete
                                fullWidth
                                id="from-warehouse-select"
                                options={communeWarehouses}
                                getOptionLabel={(warehouse) =>
                                  warehouse
                                    ? `${warehouse.name}, ${warehouse.address}`
                                    : ""
                                }
                                disabled={communeWarehouses.length === 0}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Bodega de devolución"
                                    helperText={
                                      submitPressed &&
                                      selectedFromWarehouse === null &&
                                      "Campo obligatorio"
                                    }
                                    error={
                                      submitPressed &&
                                      selectedFromWarehouse === null
                                    }
                                  />
                                )}
                                value={selectedFromWarehouse}
                                onChange={(event, newValue) =>
                                  handleSelectFromWarehouse(event, newValue)
                                }
                              />
                            </Tooltip>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </>
              )}
            {(selectedFileType === "Carga masiva" ||
              (selectedFileType === "Carga manual" &&
                ((selectedOrderType &&
                  (selectedOrderType.value === "delivery" ||
                    selectedOrderType.value === "pick_up" ||
                    selectedOrderType.value === "delivery_return")) ||
                  productSaved) &&
                ((selectedOrderType &&
                  selectedOrderType.value !== "click_collect" &&
                  selectedOrderType.value !== "pay_collect") ||
                  warehouseAvailability))) && (
              <Grid container className="container-upload-files">
                {selectedFileType === "Carga manual" ? (
                  <Grid container>
                    <Grid item xs={12} className="flex-center">
                      <Grid container className="manual-upload-container">
                        <Grid item xs={12} sm={6} className="pd-r-10 pd-l-10">
                          <TextField
                            fullWidth
                            variant="outlined"
                            label="Id Referenica*"
                            name="reference_id"
                            value={referenceId}
                            error={submitPressed && referenceId === ""}
                            helperText={
                              submitPressed && referenceId === ""
                                ? "Campo obligatorio"
                                : ""
                            }
                            onChange={(e) => setReferenceId(e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} className="pd-r-10 pd-l-10">
                          <TextField
                            fullWidth
                            error={submitPressed && recipient === ""}
                            variant="outlined"
                            label="Destinatario*"
                            name="recipient"
                            value={recipient}
                            onChange={(e) => setRecipient(e.target.value)}
                            helperText={
                              submitPressed && recipient === ""
                                ? "Campo obligatorio"
                                : ""
                            }
                          />
                        </Grid>
                        {selectedOrderType &&
                          selectedOrderType.value !== "pick_up" &&
                          selectedOrderType.value !== "pick_up_fulfillment" &&
                          selectedOrderType.value !== "delivery_return" && (
                            <>
                              <Grid
                                item
                                xs={12}
                                sm={6}
                                className="pd-r-10 pd-l-10"
                              >
                                <TextField
                                  fullWidth
                                  error={submitPressed && street === ""}
                                  variant="outlined"
                                  label="Calle*"
                                  name="street"
                                  value={street}
                                  onChange={(e) => setStreet(e.target.value)}
                                  helperText={
                                    submitPressed && street === ""
                                      ? "Campo obligatorio"
                                      : ""
                                  }
                                />
                              </Grid>

                              <Grid
                                item
                                xs={12}
                                sm={6}
                                className="pd-r-10 pd-l-10"
                              >
                                <TextField
                                  fullWidth
                                  error={submitPressed && streetNumber === ""}
                                  variant="outlined"
                                  label="Número*"
                                  name="street_number"
                                  value={streetNumber}
                                  onChange={(e) =>
                                    setStreetNumber(e.target.value)
                                  }
                                  helperText={
                                    submitPressed && streetNumber === ""
                                      ? "Campo obligatorio"
                                      : ""
                                  }
                                />
                              </Grid>
                            </>
                          )}
                        <Grid item xs={12} sm={6} className="pd-r-10 pd-l-10">
                          <TextField
                            fullWidth
                            variant="outlined"
                            label="Notas"
                            name="notes"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} className="pd-r-10 pd-l-10">
                          <InputMask
                            mask="99.999.999-9"
                            value={contactRut}
                            onChange={(e) => setContactRut(e.target.value)}
                            disabled={false}
                            maskChar=""
                          >
                            {() => (
                              <TextField
                                fullWidth
                                error={submitPressed && contactRut == ""}
                                variant="outlined"
                                label="RUT*"
                                name="contactRut"
                                helperText={
                                  submitPressed && contactRut == ""
                                    ? "Campo obligatorio"
                                    : ""
                                }
                              />
                            )}
                          </InputMask>
                        </Grid>
                        <Grid item xs={12} sm={6} className="pd-r-10 pd-l-10">
                          <InputMask
                            mask="+56 9 9999 9999"
                            value={contactPhone}
                            onChange={(e) => setContactPhone(e.target.value)}
                            disabled={false}
                            maskChar=" "
                          >
                            {() => (
                              <TextField
                                fullWidth
                                error={submitPressed && contactPhone === ""}
                                variant="outlined"
                                label="Número de contacto*"
                                name="contactPhone"
                                helperText={
                                  submitPressed && contactPhone === ""
                                    ? "Campo obligatorio"
                                    : ""
                                }
                              />
                            )}
                          </InputMask>
                        </Grid>
                        <Grid item xs={12} sm={6} className="pd-r-10 pd-l-10">
                          <TextField
                            fullWidth
                            error={
                              (submitPressed && contactEmail === "") ||
                              (contactEmail !== "" &&
                                !EmailValidator.validate(contactEmail))
                            }
                            variant="outlined"
                            label="Correo de contacto*"
                            name="contactEmail"
                            value={contactEmail}
                            onChange={(e) => setContactEmail(e.target.value)}
                            helperText={
                              submitPressed && contactEmail === ""
                                ? "Campo obligatorio"
                                : contactEmail !== "" &&
                                  !EmailValidator.validate(contactEmail)
                                ? "Formato incorrecto"
                                : ""
                            }
                          />
                        </Grid>
                        {(selectedOrderType.value === "delivery" ||
                          selectedOrderType.value === "delivery_fulfillment" ||
                          selectedOrderType.value === "pick_up" ||
                          selectedOrderType.value ===
                            "pick_up_fulfillment") && (
                          <>
                            <Grid
                              item
                              xs={12}
                              sm={6}
                              className="pd-r-10 pd-l-10"
                            >
                              <TextField
                                fullWidth
                                error={submitPressed && packages === ""}
                                variant="outlined"
                                label="Bultos*"
                                name="packages"
                                value={packages}
                                onChange={(e) => setPackages(e.target.value)}
                                helperText={
                                  submitPressed && packages === ""
                                    ? "Campo obligatorio"
                                    : ""
                                }
                              />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                ) : (
                  <>
                    <Grid item xs={12} className="flex-center">
                      <CustomDropzone
                        accept={".xlsx"}
                        width={"550px"}
                        marginTop={"50px"}
                        onDrop={handleFileDrop}
                      />
                    </Grid>
                    <Grid item xs={12} className="flex-center">
                      <Grid item className="custom-stack-container">
                        <Stack
                          direction="row"
                          spacing={1}
                          className="custom-stack"
                        >
                          {files.map((file, index) => {
                            return (
                              <Chip
                                key={index}
                                label={file.path}
                                onDelete={() => handleDelete(file.path)}
                              />
                            );
                          })}
                        </Stack>
                      </Grid>
                    </Grid>
                  </>
                )}
                <Grid item xs={12} sm={12} className="flex-center">
                  {selectedFileType === "Carga manual" && (
                    <Button
                      type="submit"
                      variant="contained"
                      onClick={handleSubmit}
                    >
                      {loading ? (
                        <CircularProgress className="loading-btn" />
                      ) : (
                        "Cargar orden"
                      )}
                    </Button>
                  )}
                  {selectedFileType === "Carga masiva" && (
                    <Button
                      type="submit"
                      variant="contained"
                      onClick={handleFileSubmit}
                      disabled={!validFile}
                    >
                      {loading ? (
                        <CircularProgress className="loading-btn" />
                      ) : (
                        "Cargar archivo"
                      )}
                    </Button>
                  )}
                </Grid>
              </Grid>
            )}
          </Grid>
          <AlertBox
            severity="success"
            messageAlert={messageAlert}
            setOpenAlert={setOpenSuccessAlert}
            openAlert={openSuccessAlert}
            hideDuration={6000}
          />
          <AlertBox
            severity="error"
            messageAlert={messageAlert}
            setOpenAlert={setOpenErrorAlert}
            openAlert={openErrorAlert}
            hideDuration={6000}
          />
          <AlertBox
            severity="error"
            messageAlert={messageAlert}
            setOpenAlert={setOpenFileErrorAlert}
            openAlert={openFileErrorAlert}
            hideDuration={6000}
          />
        </>
      )}
    </>
  );
}

export default UploadRequests;
